import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import styles from './Header.module.css';

const SERVICE_NAME = process.env.REACT_APP_SERVICE_NAME;

const Header: React.FC = () => {
  const { logout, user } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  const [isAtTop, setIsAtTop] = useState(true);
  const menuRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const lastScrollY = useRef(0);

  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    
    // Only update if scroll direction changed or crossed threshold
    if (currentScrollY !== lastScrollY.current) {
      const wasAtTop = lastScrollY.current < 10;
      const isNowAtTop = currentScrollY < 10;
      
      // Only update state if the "at top" status changed
      if (wasAtTop !== isNowAtTop) {
        setIsAtTop(isNowAtTop);
        if (!isNowAtTop && showMenu) {
          setShowMenu(false);
        }
      }
      
      lastScrollY.current = currentScrollY;
    }
  }, [showMenu]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial position
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const handleUserButtonClick = () => {
    if (!isAtTop) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => {
        setShowMenu(true);
      }, 300);
    } else {
      setShowMenu(!showMenu);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const firstLetter = user?.name?.[0]?.toUpperCase() || 
                     user?.email?.[0]?.toUpperCase() || 
                     '?';

  return (
    <header 
      ref={headerRef}
      className={`${styles.header} ${isAtTop ? styles.headerTop : ''}`}
    >
      <h1>{SERVICE_NAME} Dashboard</h1>
      <div className={styles.userMenu} ref={menuRef}>
        <button 
          className={styles.userButton}
          onClick={handleUserButtonClick}
          aria-label="User menu"
        >
          <div className={styles.userAvatar}>{firstLetter}</div>
        </button>
        {showMenu && (
          <div className={styles.dropdown}>
            <div className={styles.userInfo}>
              <div className={styles.userName}>{user?.name}</div>
              <div className={styles.userEmail}>{user?.email}</div>
            </div>
            <button onClick={logout} className={styles.logoutButton}>
              Logout
            </button>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
